import useDebouncedCallback from '@/hooks/useDebouncedCallback';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { GetLatin1HelperMessageProps } from './getLatin1HelperMessage';
import { getLatin1HelperMessage } from './getLatin1HelperMessage';
import type {
  Latin1SafeTextFieldOnBlur,
  Latin1SafeTextFieldOnChange,
  TextFieldChangeEvent,
  TextFieldFocusEvent,
  TextFieldPasteEvent,
} from './latin1SafeTextField.types';
import { sanitizeLatin1SafeInput } from './sanitizeLatin1SafeInput';
import type { FieldValidationResult } from './validateLatin1SafeInput';
import { validateLatin1SafeInput } from './validateLatin1SafeInput';

export type useLatin1SafeTextFieldProps = Omit<GetLatin1HelperMessageProps, 'showFormattingHint'> & {
  value: string | undefined | null;
  onChange?: Latin1SafeTextFieldOnChange;
  onBlur?: Latin1SafeTextFieldOnBlur;
  /** Turn off Latin1 validation and sanitisation entirely (no sanitising, validation, or hint). */
  enableLatin1?: boolean;

  /**
   * Sanitise on paste. Default true when enableLatin1 = true.
   * Blur sanitisation and validation always run when enableLatin1 is true.
   */
  sanitizeOnPaste?: boolean;

  /**
   * Maximum allowed length. Paste insertion is clamped so the value does not exceed maxLength to retain native undo / redo behaviour.
   * Passed in to htmlInput maxLength.
   */
  maxLength?: number;
};

export const useLatin1SafeTextField = ({
  value,
  onChange,
  onBlur,
  enableLatin1 = true,
  sanitizeOnPaste = true,
  maxLength,
  errorMessage,
  defaultHelperText,
  formattingHint,
}: useLatin1SafeTextFieldProps) => {
  const stringValue = String(value ?? '');

  const [showFormattingHint, setShowFormattingHint] = useState(false);
  const [validation, setValidation] = useState<FieldValidationResult>({ hasError: false });

  const skipHideHintOnNextChangeRef = useRef(false);

  const debouncedValidate = useDebouncedCallback(
    (val: string) => {
      const preview = sanitizeLatin1SafeInput(val).value;
      setValidation(validateLatin1SafeInput(preview));
    },
    300,
    [],
  );

  useEffect(() => {
    if (!enableLatin1) {
      setValidation({ hasError: false });
      return;
    }
    debouncedValidate(stringValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enableLatin1, stringValue]);

  const displayedErrorMessage = useMemo(() => {
    if (!enableLatin1) {
      return errorMessage;
    }
    const errors = [validation.hasError ? validation.errorMessage : undefined, errorMessage].filter(Boolean);

    return errors.length ? errors.join('\n') : undefined;
  }, [enableLatin1, validation.hasError, validation.errorMessage, errorMessage]);

  const displayedError = Boolean(displayedErrorMessage);

  const helperText = useMemo(
    () =>
      getLatin1HelperMessage({
        errorMessage: displayedErrorMessage,
        showFormattingHint: enableLatin1 && showFormattingHint,
        formattingHint,
        defaultHelperText,
      }),
    [displayedErrorMessage, enableLatin1, showFormattingHint, formattingHint, defaultHelperText],
  );

  const handleChange = useCallback(
    (e: TextFieldChangeEvent) => {
      if (skipHideHintOnNextChangeRef.current) {
        skipHideHintOnNextChangeRef.current = false;
      } else {
        setShowFormattingHint(false);
      }

      onChange?.(e);
    },
    [onChange],
  );

  const handlePaste = useCallback(
    (e: TextFieldPasteEvent) => {
      // Let the browser handle the paste normally if no sanitisation or normalisation
      if (!enableLatin1 || !sanitizeOnPaste) {
        return;
      }

      const clipboardText = e.clipboardData.getData('text');
      const { value: sanitized, didSanitize, didNormalize } = sanitizeLatin1SafeInput(clipboardText);

      // allow native paste if nothing changes
      if (!didSanitize && !didNormalize) {
        return;
      }

      e.preventDefault();

      const inputEl = e.currentTarget;
      const before = inputEl.value;

      const selectionStart = inputEl.selectionStart ?? before.length;
      const selectionEnd = inputEl.selectionEnd ?? before.length;

      // Clamp the paste to MAX_LENGTH and detect when nothing is inserted,
      // so the formatting hint doesn’t get stuck on oversized pastes
      const replacedLength = Math.max(0, selectionEnd - selectionStart);
      const remainingLength =
        typeof maxLength === 'number' ? Math.max(0, maxLength - (before.length - replacedLength)) : undefined;
      const textToInsert = typeof remainingLength === 'number' ? sanitized.slice(0, remainingLength) : sanitized;

      // Nothing can be inserted (already at MAX_LENGTH)
      if (!textToInsert) {
        skipHideHintOnNextChangeRef.current = false;
        return;
      }

      // Show formatting hint if sanitised (not just normalised)
      if (didSanitize) {
        skipHideHintOnNextChangeRef.current = true;
        setShowFormattingHint(true);
      }

      // Use execCommand insert text to preserve native undo/redo behaviour
      // Deprecated, but still supported across browsers
      const didInsert = document.execCommand?.('insertText', false, textToInsert) ?? false;
      if (!didInsert) {
        inputEl.setRangeText(textToInsert, selectionStart, selectionEnd, 'end');
      }

      inputEl.dispatchEvent(new Event('input', { bubbles: true }));

      // Fix edge case where rapid pasting over max length leaves the hint stuck
      if (inputEl.value === before) {
        skipHideHintOnNextChangeRef.current = false;
      }
    },
    [enableLatin1, sanitizeOnPaste, maxLength],
  );

  const handleBlur = useCallback(
    (e: TextFieldFocusEvent) => {
      if (enableLatin1) {
        const el = e.currentTarget;
        const rawValue = el.value;

        // Sanitise on blur to catch unsupported typed characters
        const { value: sanitizedValue, didSanitize, didNormalize } = sanitizeLatin1SafeInput(rawValue);

        if ((didSanitize || didNormalize) && sanitizedValue !== rawValue) {
          if (didSanitize) {
            skipHideHintOnNextChangeRef.current = true;
            setShowFormattingHint(true);
          }

          el.setRangeText(sanitizedValue, 0, rawValue.length, 'end');
          el.dispatchEvent(new Event('input', { bubbles: true }));
        }
      }

      onBlur?.(e);
    },
    [enableLatin1, onBlur],
  );

  return {
    value: stringValue,
    error: displayedError,
    helperText,
    onChange: handleChange,
    htmlInputProps: {
      onPaste: handlePaste,
      onBlur: handleBlur,
      maxLength,
    },
    showFormattingHint,
    validation,
  };
};
